package dev.lingvo.site.pages.app.projects.slug

import androidx.compose.runtime.Composable
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.gap
import dev.lingvo.shared.CreateKeyRequest
import dev.lingvo.shared.FileDto
import dev.lingvo.shared.KeyRow
import dev.lingvo.shared.PluralCategory
import dev.lingvo.shared.UpdateKeyRequest
import dev.lingvo.shared.localeInfo
import dev.lingvo.shared.pluralCategoriesFor
import dev.lingvo.site.ds.LAlert
import dev.lingvo.site.ds.LAlertVariant
import dev.lingvo.site.ds.LBracesIcon
import dev.lingvo.site.ds.LButton
import dev.lingvo.site.ds.LButtonVariant
import dev.lingvo.site.ds.LCheckIcon
import dev.lingvo.site.ds.LWarningIcon
import dev.lingvo.site.ds.LFileIcon
import dev.lingvo.site.ds.LKeyIcon
import dev.lingvo.site.ds.LModal
import dev.lingvo.site.ds.LSectionTitle
import dev.lingvo.site.ds.LSelect
import dev.lingvo.site.ds.LSelectOption
import dev.lingvo.site.ds.LSwitch
import dev.lingvo.site.ds.LTextArea
import dev.lingvo.site.ds.LTagIcon
import dev.lingvo.site.ds.LTextField
import dev.lingvo.site.ds.Space
import dev.lingvo.site.i18n.t
import dev.lingvo.site.i18n.tPlural
import dev.lingvo.site.i18n.tf
import dev.lingvo.site.i18n.trf

internal data class KeyDraft(
    val fileId: String = "",
    val name: String = "",
    val source: String = "",
    val context: String = "",
    val maxLength: String = "",
    val tags: String = "",
    val isPlural: Boolean = false,
    /**
     * One entry per CLDR category the *source* locale requires.
     *
     * A plural key used to be created from the single [source] field, written into `OTHER` alone —
     * so an English plural was born with `one` empty, incomplete in its own source language, from a
     * tool whose whole argument is that it handles plurals properly.
     */
    val sourcePluralForms: Map<PluralCategory, String> = emptyMap(),
)

@Composable
internal fun KeyModal(
    open: Boolean, editing: KeyRow?, files: List<FileDto>, draft: KeyDraft, sourceLocale: String,
    onDraft: (KeyDraft) -> Unit, onClose: () -> Unit, busy: Boolean, onSave: () -> Unit,
) {
    val maxError = if (draft.maxLength.isNotBlank() && (draft.maxLength.toIntOrNull() ?: 0) <= 0) {
        t("app.projects.slug.key_editor_modal.enter_a_positive_number", "Enter a positive number")
    } else {
        null
    }
    // What the source language actually requires, from CLDR — English asks for two forms, Russian
    // for four, Japanese for one. Asking for the wrong set is how a key ends up half-written.
    val requiredCategories = pluralCategoriesFor(sourceLocale).toList()
    val pluralComplete = !draft.isPlural ||
        requiredCategories.all { draft.sourcePluralForms[it]?.isNotBlank() == true }
    val valid = draft.name.isNotBlank() && maxError == null &&
        (editing != null || draft.fileId.isNotBlank() && (if (draft.isPlural) pluralComplete else draft.source.isNotBlank()))
    // **Saving this toggle reshapes every translation of the key, in every language.** The server
    // routes a changed `isPlural` through `TranslationRepo.reshapeForKey`: off keeps only `other`
    // and drops one, few, many, zero and two; on writes the current value into `other` and leaves
    // every other category this language requires empty — which fires MISSING_PLURAL_FORM, an
    // error by default, and blocks approval on every affected string. A Russian key with four
    // hand-written forms in eight languages went to one string per language behind a button
    // labelled "Save changes", with nothing on screen saying so.
    val reshapesTranslations = editing != null && draft.isPlural != editing.key.isPlural
    LModal(
        open = open,
        title = if (editing == null) t("app.projects.slug.key_editor_modal.add_key", "Add key") else t("app.projects.slug.key_editor_modal.edit_key", "Edit key"),
        onClose = onClose,
        actions = {
            LButton(t("app.projects.slug.key_editor_modal.cancel", "Cancel"), onClose, variant = LButtonVariant.GHOST, enabled = !busy)
            LButton(
                if (editing == null) t("app.projects.slug.key_editor_modal.add_key", "Add key") else t("app.projects.slug.key_editor_modal.save_changes", "Save changes"),
                onSave,
                variant = if (reshapesTranslations) LButtonVariant.DANGER else LButtonVariant.PRIMARY,
                enabled = valid && !busy,
                loading = busy,
                // The glyph changes with the consequence: a save that reshapes every translation is
                // not the same act as a save, and the button already changes colour to say so.
                leadingIcon = { if (reshapesTranslations) LWarningIcon() else LCheckIcon() },
            )
        },
    ) {
        // **Four groups, in the order somebody fills them.** Eight controls in one column made the
        // plural switch — the one that reshapes every translation of this key in every language —
        // the seventh unlabelled row in a stack, immediately after a free-text tag field.
        Column(Modifier.fillMaxWidth().gap(Space.MD)) {
            LSectionTitle(t("app.projects.slug.key_editor_modal.identity", "Identity"), icon = { LKeyIcon() })
            if (editing == null) LSelect(draft.fileId, files.map { LSelectOption(it.id, it.path) }, { onDraft(draft.copy(fileId = it)) }, label = t("app.projects.slug.key_editor_modal.file", "File"))
            LTextField(draft.name, { onDraft(draft.copy(name = it)) }, label = t("app.projects.slug.key_editor_modal.key_name", "Key name"), placeholder = t("app.projects.slug.key_editor_modal.checkout_actions_pay_now", "checkout.actions.pay_now"))
        }
        if (editing == null) {
            Column(Modifier.fillMaxWidth().gap(Space.MD)) {
                LSectionTitle(t("app.projects.slug.key_editor_modal.content", "Content"), icon = { LFileIcon() })
                if (draft.isPlural) {
                    // One field per required category, all visible at once rather than behind tabs:
                    // plural forms are written by comparing them, and a form you cannot see beside the
                    // others is the one that ends up inconsistent.
                    requiredCategories.forEach { category ->
                        LTextArea(
                            value = draft.sourcePluralForms[category].orEmpty(),
                            onValueChange = { value ->
                                onDraft(draft.copy(sourcePluralForms = draft.sourcePluralForms + (category to value)))
                            },
                            label = trf(
                                "app.projects.slug.key_editor_modal.source_category",
                                "Source · {category}",
                                "category" to category.name.lowercase(),
                            ),
                            placeholder = when (category) {
                                PluralCategory.ONE -> "{count} item" // i18n-ignore: placeholder syntax shown to the writer
                                else -> "{count} items" // i18n-ignore: placeholder syntax shown to the writer
                            },
                        )
                    }
                } else {
                    LTextArea(draft.source, { onDraft(draft.copy(source = it)) }, label = t("app.projects.slug.key_editor_modal.source_string", "Source string"), placeholder = t("app.projects.slug.key_editor_modal.pay_now", "Pay now"))
                }
            }
        }
        Column(Modifier.fillMaxWidth().gap(Space.MD)) {
            LSectionTitle(t("app.projects.slug.key_editor_modal.metadata", "Metadata"), icon = { LTagIcon() })
            LTextArea(draft.context, { onDraft(draft.copy(context = it)) }, label = t("app.projects.slug.key_editor_modal.context", "Context"), placeholder = t("app.projects.slug.key_editor_modal.shown_on_the_checkout_confirmation_button", "Shown on the checkout confirmation button"))
            LTextField(draft.maxLength, { onDraft(draft.copy(maxLength = it)) }, label = t("app.projects.slug.key_editor_modal.maximum_length", "Maximum length"), placeholder = t("app.projects.slug.key_editor_modal.optional", "Optional"), error = maxError)
            LTextField(draft.tags, { onDraft(draft.copy(tags = it)) }, label = t("app.projects.slug.key_editor_modal.tags", "Tags"), placeholder = t("app.projects.slug.key_editor_modal.checkout_mobile", "checkout, mobile"))
        }
        Column(Modifier.fillMaxWidth().gap(Space.MD)) {
            LSectionTitle(t("app.projects.slug.key_editor_modal.shape", "Shape"), icon = { LBracesIcon() })
            LSwitch(draft.isPlural, { onDraft(draft.copy(isPlural = it)) }, t("app.projects.slug.key_editor_modal.plural_key", "Plural key"))
            // **Directly under the switch that causes it.** The warning used to be the last element
            // in the dialog, below three unrelated fields, so the consequence and the control that
            // produces it were never on screen as one thing.
            if (reshapesTranslations) {
                LAlert(
                    title = if (draft.isPlural) {
                        t(
                            "app.projects.slug.key_editor_modal.turning_plurals_on_empties_every_other_form",
                            "Turning plurals on empties every other form",
                        )
                    } else {
                        t(
                            "app.projects.slug.key_editor_modal.turning_plurals_off_discards_every_other_form",
                            "Turning plurals off discards every other form",
                        )
                    },
                    description = if (draft.isPlural) {
                        t("app.projects.slug.key_editor_modal.each_languages_current_translation_becomes_its", "Each language's current translation becomes its \"other\" form and every remaining category that language requires starts empty. Those empties block approval until somebody writes them.")
                    } else {
                        t("app.projects.slug.key_editor_modal.only_the_other_form_of_each", "Only the \"other\" form of each translation is kept. Every one, few, many, zero and two form of this key is discarded, in every language, and nothing on this screen can bring them back.")
                    },
                    modifier = Modifier.fillMaxWidth(),
                    variant = LAlertVariant.DANGER,
                    // Shown the moment the plural switch is flipped, and it warns that saving
                    // will empty every other form. A consequence that arrives silently is one the
                    // reader agrees to without hearing it.
                    live = true,
                )
            }
            if (draft.isPlural && editing == null) {
                LAlert(
                    title = tPlural(
                        "app.projects.slug.key_editor_modal.language_needs_forms",
                        requiredCategories.size,
                        "{language} needs {count} form",
                        "{language} needs {count} forms",
                        "language" to localeInfo(sourceLocale).englishName,
                    ),
                    description = tf(
                        "app.projects.slug.key_editor_modal.cldr_requires",
                        "CLDR requires {categories} for this language. A key saved without all of them " +
                            "is incomplete in its own source language, and every target language " +
                            "inherits the gap.",
                        "categories" to requiredCategories.joinToString(", ") { it.name.lowercase() },
                    ),
                    modifier = Modifier.fillMaxWidth(),
                    variant = LAlertVariant.INFO,
                )
            }
        }
    }
}

private fun KeyDraft.tagsList(): List<String> = tags.split(',').map(String::trim).filter(String::isNotBlank).distinct()

internal fun KeyDraft.createRequest() = CreateKeyRequest(
    fileId = fileId,
    name = name.trim(),
    context = context.trim().ifBlank { null },
    isPlural = isPlural,
    maxLength = maxLength.toIntOrNull(),
    tags = tagsList(),
    sourceValue = source.trim().takeUnless { isPlural },
    sourcePluralForms = if (isPlural) {
        sourcePluralForms.mapValues { (_, value) -> value.trim() }.filterValues(String::isNotBlank)
    } else {
        null
    },
)

internal fun KeyDraft.updateRequest() = UpdateKeyRequest(
    name = name.trim(),
    context = context.trim().ifBlank { null },
    maxLength = maxLength.toIntOrNull(),
    tags = tagsList(),
    isPlural = isPlural,
)
